import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

const STORAGE_KEY = 'sidebarOpen';

/**
 * Shared state for the left-hand navigation drawer.
 *
 * Both `app-side-navigation` (the drawer itself) and `app-header`
 * (which renders the hamburger toggle in the top bar) consume this
 * service, so collapse/expand stays in sync from either end.
 */
@Injectable({ providedIn: 'root' })
export class SidenavStateService {
  private readonly _isOpen$ = new BehaviorSubject<boolean>(this.readInitial());
  /** Reactive stream of the current open/closed state. */
  readonly isOpen$: Observable<boolean> = this._isOpen$.asObservable();

  /** Snapshot accessor (useful for template `value$ | async` alternatives). */
  get isOpen(): boolean {
    return this._isOpen$.value;
  }

  /** Toggle the drawer between expanded and collapsed. */
  toggle(): void {
    this.set(!this._isOpen$.value);
  }

  /** Explicitly set the open state. */
  set(open: boolean): void {
    if (this._isOpen$.value === open) return;
    this._isOpen$.next(open);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(open));
    } catch {
      /* localStorage may be unavailable (private mode); ignore. */
    }
  }

  private readInitial(): boolean {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      // Default to expanded if no preference is stored yet.
      return raw === null ? true : raw === 'true';
    } catch {
      return true;
    }
  }
}
